import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useHostAuth from '../hooks/useHostAuth';
import { safeNavigate } from '../utils/safeNavigate';
import LandingHeader from '../components/landingSnapshot/LandingHeader.jsx';
import PublicSiteFooter from '../../components/Footer/PublicSiteFooter.jsx';

const TabButton = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-semibold ${
      active ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
    }`}
  >
    {children}
  </button>
);

const HostLoginView = ({ hostAuth }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('phone');
  const [form, setForm] = useState({ country_code: '+91', phone: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!hostAuth.loading && hostAuth.host) {
      safeNavigate(navigate, '/host/dashboard', { replace: true });
    }
  }, [hostAuth.loading, hostAuth.host, navigate]);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const payload =
        mode === 'email'
          ? { identifier_type: 'email', email: form.email, password: form.password }
          : { identifier_type: 'phone', country_code: form.country_code, phone: form.phone, password: form.password };
      await hostAuth.login(payload);
      safeNavigate(navigate, '/host/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <LandingHeader />
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-3xl bg-white shadow-lg rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-700">Homevilas</p>
            <h1 className="text-xl font-bold text-slate-900">Host Login</h1>
            <p className="text-sm text-slate-600">Access your host workspace with phone or email.</p>
          </div>
        </div>

        {error && <div className="mt-4 rounded-lg bg-rose-50 text-rose-700 px-4 py-2 text-sm">{error}</div>}

        <div className="mt-6 grid md:grid-cols-[1.1fr_0.9fr] gap-6">
          <div className="space-y-4">
            <div className="flex gap-2">
              <TabButton active={mode === 'phone'} onClick={() => setMode('phone')}>
                Phone
              </TabButton>
              <TabButton active={mode === 'email'} onClick={() => setMode('email')}>
                Email
              </TabButton>
            </div>
            <form className="space-y-3" onSubmit={submit}>
              {mode === 'phone' ? (
                <div className="flex gap-2">
                  <input
                    className="w-24 px-3 py-2 rounded-lg border border-slate-300"
                    value={form.country_code}
                    onChange={(e) => setForm((prev) => ({ ...prev, country_code: e.target.value }))}
                    placeholder="+91"
                  />
                  <input
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-300"
                    value={form.phone}
                    onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone number"
                    required
                  />
                </div>
              ) : (
                <input
                  type="email"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="Email"
                  required
                />
              )}
              <input
                type="password"
                className="w-full px-3 py-2 rounded-lg border border-slate-300"
                value={form.password}
                onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                placeholder="Password"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-emerald-600 text-white rounded-lg py-2 font-semibold hover:bg-emerald-700 disabled:opacity-70"
              >
                {submitting ? 'Signing in...' : 'Login'}
              </button>
            </form>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">Production access</p>
            <p className="mt-2">
              Sign in with your approved host account details.
            </p>
            <div className="mt-4 text-xs text-slate-500">
              If access is not available, contact the Homevilas admin team.
            </div>
          </div>
        </div>
      </div>
      </div>
      <PublicSiteFooter />
    </>
  );
};

const HostLoginWithAuth = () => {
  const hostAuth = useHostAuth();
  return <HostLoginView hostAuth={hostAuth} />;
};

const HostLogin = ({ hostAuth }) => {
  if (hostAuth) {
    return <HostLoginView hostAuth={hostAuth} />;
  }
  return <HostLoginWithAuth />;
};

export default HostLogin;

